import React, { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/DataTable';
import { Chart, ArcElement, BarElement, LineElement, CategoryScale, LinearScale, Tooltip, Legend, DoughnutController, BarController, LineController, PointElement } from 'chart.js';

// Register all necessary components
Chart.register(ArcElement, BarElement, LineElement, CategoryScale, LinearScale, Tooltip, Legend, DoughnutController, BarController, LineController, PointElement);

// Sample data for top donations
const topDonations = [
    { donations: 32, program: 'New Program A', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 65, program: 'New Program B', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 73, program: 'New Program C', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 231, program: 'New Program D', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 12, program: 'New Program E', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 12, program: 'New Program E', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 12, program: 'New Program E', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
    { donations: 12, program: 'New Program E', amountReceived: '$2,428.69', netReceived: '$2,428.69', fee: '$213.34' },
];

// Sample report entries
const donationReport = Array.from({ length: 10 }, (_, i) => ({
    id: String(i + 1),
    date: '05-05-2025',
    program: 'New Program',
    type: 'Programs',
    method: 'AID Pay Bank',
    donation: '$10.00',
    netReceived: '$9.87',
    fee: '$0.13',
    customer: { name: 'Mohammed W Ahmed', email: 'pay2@iashine.com' }
}));

export default function ReportsPage() {
    const [startDate, setStartDate] = useState('2025-05-01');
    const [endDate, setEndDate] = useState('2025-05-27');
    const paymentRef = useRef(null);
    const paymentChartInstance = useRef(null);

    useEffect(() => {
        if (paymentChartInstance.current) paymentChartInstance.current.destroy();
        paymentChartInstance.current = new Chart(paymentRef.current, {
            type: 'doughnut',
            data: {
                labels: ['Programs', 'Services', 'Events'],
                datasets: [{
                    data: [231, 65, 33],
                    backgroundColor: ['#a0522d', '#ffa500', '#ffd580']
                }]
            },
            options: { plugins: { legend: { position: 'right' } } }
        });
    }, []);

    const topColumns = [
        { key: 'donations', title: 'DONATIONS' },
        { key: 'program', title: 'PROGRAM/SERVICE/EVENT' },
        { key: 'amountReceived', title: 'AMOUNT RECEIVED' },
        { key: 'netReceived', title: 'NET AMOUNT RECEIVED' },
        { key: 'fee', title: 'FEE' }
    ];

    const reportColumns = [
        { key: 'date', title: 'DATE' },
        { key: 'program', title: 'PROGRAM/SERVICE/EVENT' },
        { key: 'type', title: 'TYPE' },
        { key: 'method', title: 'PAYMENT METHOD' },
        { key: 'donation', title: 'DONATION' },
        { key: 'netReceived', title: 'NET RECEIVED' },
        { key: 'fee', title: 'FEE' },
        {
            key: 'customer', title: 'CUSTOMER', render: (_, row) => <>
                <div>{row.customer.name}</div>
                <div style={styles.email}>{row.customer.email}</div>
            </>
        }
    ];

    return (
        <>
            <div style={styles.container}>
                <h1 style={styles.title}>Reports</h1>

                <div style={styles.topRow}>
                    <div style={{...styles.cardSmall, maxWidth: 400}}>
                        <h3 style={styles.cardTitle}>All Time Donations By Type</h3>
                        <div>
                            <canvas ref={paymentRef} style={styles.chart} />
                        </div>
                    </div>

                    <div style={styles.cardSmall}>
                        <h3 style={styles.cardTitle}>All Time Top Donations</h3>
                        <DataTable data={topDonations} columns={topColumns} rowKey="program" />
                    </div>
                </div>

                <div style={styles.cardFull}>
                    <h3 style={styles.cardTitle}>Donation Report</h3>
                    <div style={styles.filterRow}>
                        <div style={styles.dateGroup}>
                            <label style={styles.filterLabel}>Start Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.dateInput} />
                        </div>
                        <div style={styles.dateGroup}>
                            <label style={styles.filterLabel}>End Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.dateInput} />
                        </div>
                        <button style={styles.btnReport}>Get Report</button>
                        <button style={styles.iconBtn}><span className="material-icons">file_download</span></button>
                    </div>

                    <DataTable data={donationReport} columns={reportColumns} rowKey="id" />
                </div>
            </div>
        </>
    );
}

const styles = {
    container: { padding: 0 },
    title: { fontSize: 24, marginBottom: 16, color: '#333' },
    topRow: { display: 'flex', gap: 16, marginBottom: 16 },
    cardSmall: { background: '#fff', borderRadius: 8, padding: 16, flex: 1 },
    cardFull: { background: '#fff', borderRadius: 8, padding: 16 },
    cardTitle: { margin: 0, marginBottom: 12, fontSize: 16 },
    chart: { width: '100%', height: '200px' },
    filterRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
    dateGroup: { display: 'flex', flexDirection: 'column' },
    filterLabel: { fontSize: 14, marginBottom: 4 },
    dateInput: { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4 },
    btnReport: { background: '#7c4dff', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', marginTop: 25 },
    iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, marginTop: 30 },
    email: { fontSize: 12, color: '#777' }
};
